import React, {useCallback, useEffect, useState} from 'react';
import {Skeleton} from 'antd';
import {useTranslation} from 'react-i18next';
import {APIv1} from '../../api';
import './VpsBalance.scss';
import {images} from "../../constants";

const MAX_POLL_ATTEMPTS = 15;
const HOURS_IN_MONTH = 24 * 30;
const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;

const VpsBalance = ({userData}) => {
    const {t} = useTranslation();
    const [vpsBalance, setVpsBalance] = useState(null);
    const [isVpsBalanceLoading, setIsVpsBalanceLoading] = useState(false);
    const [hasVpsBalancePermission, setHasVpsBalancePermission] = useState(true);
    const isHead = userData?.data?.is_head === true;

    useEffect(() => {
        setHasVpsBalancePermission(true);
        setVpsBalance(null);
    }, [isHead, userData?.token]);

    const buildAuthHeaders = useCallback(() => {
        const headers = {};

        if (userData?.token) {
            headers.Authorization = `Token ${userData.token}`;
        }

        return headers;
    }, [userData?.token]);

    const getVpsBalance = useCallback(async () => {
        const response = await APIv1.get('/vps/balance/', {
            headers: buildAuthHeaders(),
            withCredentials: true,
        });

        return response.data;
    }, [buildAuthHeaders]);

    const refreshVpsBalance = useCallback(async () => {
        const response = await APIv1.post('/vps/balance/refresh/', null, {
            headers: buildAuthHeaders(),
            withCredentials: true,
        });

        return response.data;
    }, [buildAuthHeaders]);

    useEffect(() => {
        if (!userData?.token || !isHead) {
            return undefined;
        }

        let isMounted = true;
        let pollIntervalId;
        let pollAttempts = 0;
        setIsVpsBalanceLoading(true);

        const updateBalance = (data) => {
            if (!isMounted) {
                return;
            }

            setVpsBalance(data);
            setIsVpsBalanceLoading(Boolean(data?.refreshing || data?.stale));
        };

        const stopPolling = () => {
            if (pollIntervalId) {
                clearInterval(pollIntervalId);
                pollIntervalId = null;
            }
        };

        const handleBalanceError = (err) => {
            if (err?.response?.status === 403) {
                if (isMounted) {
                    setHasVpsBalancePermission(false);
                    setIsVpsBalanceLoading(false);
                }
                stopPolling();
                return;
            }

            console.error('Something went wrong loading VPS balance:', err);
            if (isMounted) {
                setIsVpsBalanceLoading(false);
            }
            stopPolling();
        };

        const pollBalance = async () => {
            try {
                pollAttempts += 1;
                const data = await getVpsBalance();
                updateBalance(data);

                if (!data?.refreshing || pollAttempts >= MAX_POLL_ATTEMPTS) {
                    if (isMounted) {
                        setIsVpsBalanceLoading(false);
                    }
                    stopPolling();
                }
            } catch (err) {
                handleBalanceError(err);
            }
        };

        const loadBalance = async () => {
            try {
                const cached = await getVpsBalance();
                updateBalance(cached);

                const refreshed = await refreshVpsBalance();
                updateBalance(refreshed);

                if (refreshed?.refreshing) {
                    pollIntervalId = setInterval(pollBalance, 2000);
                } else if (isMounted) {
                    setIsVpsBalanceLoading(false);
                }
            } catch (err) {
                handleBalanceError(err);
            }
        };

        loadBalance();

        return () => {
            isMounted = false;
            stopPolling();
        };
    }, [getVpsBalance, isHead, refreshVpsBalance, userData?.token]);

    const formatVpsNumber = (value, maximumFractionDigits = 2, minimumFractionDigits = 0) => {
        if (value === null || value === undefined || value === '') {
            return t('common.notAvailable');
        }

        const numericValue = Number(value);

        if (!Number.isFinite(numericValue)) {
            return t('common.notAvailable');
        }

        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits,
            maximumFractionDigits,
        })
            .format(numericValue)
            .replace(/\u00a0|\u202f/g, ' ');
    };

    const formatVpsPrediction = (hours) => {
        if (hours === null || hours === undefined || hours === '') {
            return t('common.notAvailable');
        }

        const numericHours = Number(hours);

        if (!Number.isFinite(numericHours)) {
            return t('common.notAvailable');
        }

        const absoluteHours = Math.abs(numericHours);
        let value = numericHours;
        let unit = 'hours';
        let maximumFractionDigits = 1;
        let minimumFractionDigits = 0;

        if (absoluteHours >= HOURS_IN_MONTH) {
            value = numericHours / HOURS_IN_MONTH;
            unit = 'months';
            minimumFractionDigits = 1;
        } else if (absoluteHours >= HOURS_IN_DAY) {
            value = numericHours / HOURS_IN_DAY;
            unit = 'days';
        } else if (absoluteHours < 1) {
            value = numericHours * MINUTES_IN_HOUR;
            unit = 'minutes';
            maximumFractionDigits = 0;
        }

        return t('header.vpsBalance.prediction', {
            value: formatVpsNumber(value, maximumFractionDigits, minimumFractionDigits),
            unit: t(`header.vpsBalance.units.${unit}`),
        });
    };

    if (!userData?.token || !isHead || !hasVpsBalancePermission) {
        return null;
    }

    return (
        <div
            className={`vps-balance${isVpsBalanceLoading ? ' vps-balance--loading' : ''}`}
            title={t('header.vpsBalance.title')}
        >
            <img
                className="vps-balance__logo"
                src={images.servercoreLogo}
                alt={t('header.vpsBalance.provider')}
            />
            {vpsBalance ? (
                <>
                    <span className="vps-balance__value">
                        {t('header.vpsBalance.balance', {value: formatVpsNumber(vpsBalance.balance, 2, 2)})}
                    </span>
                    <span className="vps-balance__prediction">
                        {formatVpsPrediction(vpsBalance.prediction)}
                    </span>
                </>
            ) : (
                <Skeleton.Input
                    active
                    size="small"
                    className="vps-balance__skeleton"
                />
            )}
        </div>
    );
};

export default VpsBalance;
